import React, { useEffect, useRef, useState } from 'react';
import { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/cloudflare';
import '~/styles/onboarding.css';
import { json } from '@remix-run/cloudflare';
import { useLoaderData } from '@remix-run/react';
import EmailOnboarding from '~/components/ui/EmailOnboarding';
import GoogleOnboarding from '~/components/ui/GoogleOnboarding';
import OnboardingFooter from '~/components/ui/OnboardingFooter';
import FeatureShowcase from '~/components/ui/FeatureShowcase';
import PricingSection from '~/components/ui/PricingSection';
import GreenPill from '~/components/ui/GreenPill';
import { userStore } from '~/lib/stores/user';
import { callLogoutAPI } from '~/components/header/Header.client';
import { faqData } from '~/utils/faqData';
import { getCurrencyFromCountry, type SupportedCurrency } from '~/utils/currency';
import IconsMapping from '@/components/ui/Icons/IconsMapping';
import FeatureShowcaseNew from '~/components/ui/FeatureShowCaseNew';

// import Wave from '~/assets/wave.svg';

// Figma design image assets
const imgScreenshot = '/images/fallback.avif';
const videoBackground = '/videos/bgVideo.mp4';

// these are avif -> put in public as webp
const imgImage2 = '/images/explore.webp';
const imgImage3 = '/images/gym.webp';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  // Try to get country from Cloudflare headers
  let detectedCurrency: SupportedCurrency = 'USD';

  try {
    // Cloudflare provides the country code in the CF-IPCountry header
    const countryCode = request.headers.get('x-vercel-ip-country') || request.headers.get('CF-IPCountry') || 'US';

    if (countryCode && countryCode !== 'XX') {
      detectedCurrency = getCurrencyFromCountry(countryCode);
    }
  } catch (error) {
    console.error('Error detecting currency:', error);
  }

  return json({ currency: detectedCurrency });
};

export const meta: MetaFunction = () => {
  return [
    { title: 'Idea to App in minutes with Dualite' },

    // Preload critical hero image for faster LCP
    {
      tagName: 'link',
      rel: 'preload',
      href: '/images/LoginBGnew.webp',
      as: 'image',
      fetchpriority: 'high',
    },
  ];
};

// Project Cards Data Structure
interface ProjectCard {
  imgUrl: string;
  link: string;
}

const ProjectCards: ProjectCard[] = [
  {
    imgUrl: '/images/red.webp',
    link: 'https://seo-hero-section-q6hd-dualite.netlify.app/',
  },
  {
    imgUrl: imgImage3,
    link: 'https://ymnasium-website-bzsb-dualite.netlify.app',
  },
  {
    imgUrl: '/images/newTemp1.webp',
    link: 'https://seo-hero-section-q6hd-dualite.netlify.app/',
  },
  {
    imgUrl: imgImage2,
    link: 'https://orm-landing-page-58wu-dualite.netlify.app',
  },
  {
    imgUrl: '/images/purple.webp',
    link: 'https://seo-dualite-9mxr-dualite.netlify.app/',
  },
  {
    imgUrl: '/images/talent.webp',
    link: 'https://www.talentmind.tech/',
  },
];

// Testimonials Data Structure
interface Testimonial {
  quote: string;
  author: string;
  company: string;
}

const Testimonials: Testimonial[] = [
  {
    quote: 'Dualite is absolutely magical.',
    author: 'LS Venkatesh',
    company: 'India',
  },
  {
    quote: 'The fastest way to prototype my ideas.',
    author: 'Michael Roberts',
    company: 'United States',
  },
  {
    quote: 'I made my first app in minutes with Dualite!',
    author: 'Augusto Silva',
    company: 'Brazil',
  },
];

export default function Onboarding() {
  const { currency } = useLoaderData<typeof loader>();
  const [email, setEmail] = useState('');
  const [view, setView] = useState<'landing' | 'email'>('landing');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  // Start at the middle set to allow seamless looping in both directions
  const [currentIndex, setCurrentIndex] = useState(ProjectCards.length);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isAutoplaying, setIsAutoplaying] = useState(true);

  // Testimonial rotation state
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isTestimonialFading, setIsTestimonialFading] = useState(false);

  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const [translateX, setTranslateX] = useState(0);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const dummyNormalRef = useRef<HTMLDivElement>(null);
  const dummyCenterRef = useRef<HTMLDivElement>(null);

  // Create infinite loop array by duplicating cards
  const infiniteCards = [...ProjectCards, ...ProjectCards, ...ProjectCards];

  const logOutSession = async () => {
    await callLogoutAPI();
  };

  useEffect(() => {
    const supabaseToken = localStorage.getItem('supabase_access_token');
    localStorage.clear();

    // Clear user data from store
    console.log('[Header] Clearing user data from store');
    userStore.set({
      email: '',
      isSubscribed: false,
      maxPrompts: '0',
      promptsUsed: '0',
      planName: 'Free',
      name: '',
      inviteCode: '',
      topUpPrompts: '0',
      topUpPromptsUsed: '0',
      created_at: new Date(),
    });
    logOutSession();

    const urlPrompt = new URLSearchParams(window.location.search).get('prompt');

    if (urlPrompt) {
      localStorage.setItem('prompt', urlPrompt);
    }

    if (supabaseToken) {
      localStorage.setItem('supabase_access_token', supabaseToken);
    }
  }, []);

  // Calculate carousel position dynamically
  useEffect(() => {
    const calculatePosition = () => {
      if (!leftPanelRef.current || !dummyNormalRef.current || !dummyCenterRef.current) {
        return;
      }

      const containerWidth = leftPanelRef.current.offsetWidth;
      const normalWidth = dummyNormalRef.current.offsetWidth;
      const centerWidth = dummyCenterRef.current.offsetWidth;

      // Measure gap dynamically from the dummy container which shares the same classes
      const gapStr = window.getComputedStyle(dummyNormalRef.current.parentElement!).gap;
      const gap = parseFloat(gapStr) || 0;

      /*
       * Calculate distance to the center of the current card
       * All cards before currentIndex are "normal" size
       */
      const distToCardStart = currentIndex * (normalWidth + gap);
      const cardCenter = distToCardStart + centerWidth / 2;

      /*
       * We want cardCenter to be at containerWidth / 2
       * offset = (containerWidth / 2) - cardCenter
       */
      const newTranslateX = containerWidth / 2 - cardCenter;

      setTranslateX(newTranslateX);
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);

    return () => window.removeEventListener('resize', calculatePosition);
  }, [currentIndex]);

  // Carousel periodic switching with slide animation
  useEffect(() => {
    if (!isAutoplaying) {
      return () => {
        clearInterval(interval);
      };
    }

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }, 3000); // Switch every 3 seconds

    return () => {
      clearInterval(interval);
    };
  }, [isAutoplaying]);

  // Reset position when reaching the end of second set to maintain infinite loop
  useEffect(() => {
    if (currentIndex >= 2 * ProjectCards.length) {
      // Wait for transition to complete, then instantly reset to middle set
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(ProjectCards.length);

        /*
         * Re-enable transition after reset
         * We need to let the render happen with transition: none
         * Then next tick re-enable
         */
        setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
      }, 600); // Match transition duration
    }
  }, [currentIndex]);

  // Testimonial rotation - switch every 5 seconds with fade animation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTestimonialFading(true);

      // After fade out animation, switch testimonial
      setTimeout(() => {
        setCurrentTestimonialIndex((prev) => (prev + 1) % Testimonials.length);
        setIsTestimonialFading(false);
      }, 600); // Match CSS fade-out animation duration
    }, 5000); // Switch every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-send OTP when email view is shown
  useEffect(() => {
    if (view === 'email' && email) {
      console.log('[Onboarding] === Auto-sending OTP ===');
      console.log('[Onboarding] Email:', email);

      const sendOTP = async () => {
        setIsSendingOTP(true);

        try {
          console.log('[Onboarding] Sending OTP request...');

          const response = await fetch('/api/otp/send', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });

          console.log('[Onboarding] OTP send response status:', response.status);

          const data = await response.json();
          console.log('[Onboarding] OTP send response data:', data);
          setSuccess(true);

          if (!response.ok) {
            console.error('[Onboarding] Failed to send OTP:', (data as { error: string }).error);

            // You might want to show an error message to the user here
            return;
          }

          console.log('[Onboarding] OTP sent successfully');
        } catch (error) {
          console.error('[Onboarding] === Error Auto-sending OTP ===');
          console.error('[Onboarding] Error type:', error instanceof Error ? error.constructor.name : typeof error);
          console.error('[Onboarding] Error message:', error instanceof Error ? error.message : error);
          console.error('[Onboarding] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
        } finally {
          setIsSendingOTP(false);
        }
      };

      sendOTP();
    }
  }, [view, email]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCheckingEmail(true);
    console.log('[Onboarding] === Email Submit Process ===');
    console.log('[Onboarding] Email:', email);

    if (!email) {
      console.error('[Onboarding] No email provided');
      return;
    }

    try {
      const data = await fetch('/api/check-email', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      const { isBlocked } = (await data.json()) as { isBlocked: boolean };

      if (isBlocked) {
        setIsBlocked(true);
        return;
      }
    } catch (error) {
      console.error('[Onboarding] Error checking email:', error);
    } finally {
      setIsCheckingEmail(false);
    }

    setView('email');
  };

  const handleBackToLogin = () => {
    console.log('[Onboarding] Going back to landing view');
    setView('landing');
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Carousel navigation functions
  const handlePrevious = () => {
    setIsAutoplaying(false);
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    setIsAutoplaying(false);
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleDotClick = (index: number) => {
    setIsAutoplaying(false);
    setIsTransitioning(true);
    setCurrentIndex(ProjectCards.length + index);
  };

  // Get card styles based on position relative to center
  const getCardStyles = (index: number) => {
    // The center card is always the middle one in the viewport (currentIndex)
    const centerPosition = currentIndex;
    const isCenter = index === centerPosition;

    return {
      border: isCenter ? '6px solid rgba(255,255,255,0.5)' : '2px solid rgba(255,255,255,0.1)',
      borderRadius: isCenter ? '9px' : '6px',
      width: isCenter ? 'clamp(305px, 42.7vw, 587px)' : 'clamp(160px, 24vw, 321px)',
      height: isCenter ? 'clamp(183px, 26.7vw, 365px)' : 'clamp(100px, 14.5vw, 200px)',
      opacity: isCenter ? 1 : 0.7,
      background: isCenter ? 'rgba(52, 52, 52, 0.7)' : undefined,
    };
  };

  return (
    <div className="min-h-screen bg-[#10110C] flex flex-col overflow-x-hidden">
      <div className="flex flex-col lg:flex-row items-center px-4 py-12 sm:px-6 sm:py-16 lg:p-8 gap-8 sm:gap-12 lg:gap-12 xl:gap-20 2xl:gap-[106px] relative overflow-x-hidden">
        {/* Left Panel - App Preview with Stats - Visible on all screens */}
        <div
          ref={leftPanelRef}
          className="mt-6 md:mt-0 w-full lg:w-[47vw] h-[42vh] sm:h-[50vh] lg:h-[90vh] flex-shrink-0 relative overflow-hidden bg-[#0F0F10] border border-[rgba(25,25,25,0.8)] rounded-[18px]"
        >
          {/* Background Pattern
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-[inherit]">
          <div
            className="absolute"
            style={{
              transform: 'rotate(-5.067deg) skewX(5.206deg)',
              width: '1299.161px',
              height: '901.146px',
              left: '-50%',
              top: '-20%',
            }}
          >
            <img src={imgPattern} alt="" className="w-full h-full object-cover" />
          </div>
        </div> */}

          {/* Main Video Background with Image Fallback */}
          <div className="absolute inset-0 z-10">
            <video
              src={videoBackground}
              poster={imgScreenshot}
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              onError={(e) => {
                // Hide video and show fallback image if video fails to load
                const video = e.currentTarget;
                video.style.display = 'none';

                const fallback = video.nextElementSibling as HTMLImageElement;

                if (fallback) {
                  fallback.style.display = 'block';
                }
              }}
            />
            {/* Fallback image in case video doesn't load */}
            <img
              src={imgScreenshot}
              alt="App Preview"
              className="w-full h-full object-cover absolute inset-0"
              style={{ display: 'none' }}
            />
            {/* Black overlay with 25% opacity */}
            <div className="absolute inset-0 bg-black opacity-30" />
          </div>

          {/* Testimonials Text */}
          <div className="w-full text-center absolute top-5 sm:top-8 lg:top-16 z-20 px-4">
            <div
              className={isTestimonialFading ? 'testimonial-fade-out' : 'testimonial-fade-in'}
              key={currentTestimonialIndex}
            >
              <p
                className="text-white/70 text-lg sm:text-xl lg:text-2xl xl:text-3xl font-normal leading-relaxed mb-1 sm:mb-2"
                style={{ fontFamily: 'Inter Display' }}
              >
                "{Testimonials[currentTestimonialIndex].quote}"
              </p>
              <p
                className="text-white/70 text-xs sm:text-sm lg:text-base font-normal"
                style={{ fontFamily: 'Inter Display' }}
              >
                ~ <span className="font-medium">{Testimonials[currentTestimonialIndex].author}</span>,{' '}
                {Testimonials[currentTestimonialIndex].company}
              </p>
            </div>
          </div>

          {/* Carousel Images */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 z-20 overflow-hidden w-full">
            {/* Dummy elements for measurement - Invisible but rendered to measure exact sizes */}
            <div className="absolute opacity-0 pointer-events-none flex gap-4 lg:gap-[24px] -z-50">
              {/* Normal Card Dummy (index 0 is never center when currentIndex >= 5) */}
              <div ref={dummyNormalRef} style={{ ...getCardStyles(0), transition: 'none' }} />
              {/* Center Card Dummy (simulate center position) */}
              <div ref={dummyCenterRef} style={{ ...getCardStyles(currentIndex), transition: 'none' }} />
            </div>

            <div
              className="flex gap-4 lg:gap-[24px] items-center will-change-transform"
              style={{
                transform: `translateX(${translateX}px)`,
                transition: isTransitioning ? 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
              }}
            >
              {infiniteCards.map((card, index) => {
                const styles = getCardStyles(index);

                /*
                 * Preload the center and adjacent cards, lazy load the rest
                 */
                const centerPosition = currentIndex;
                const isNearCenter = Math.abs(index - centerPosition) <= 2;
                return (
                  <div
                    key={index}
                    className="overflow-hidden flex-shrink-0 cursor-pointer bg-black"
                    style={{
                      ...styles,
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                    }}
                    onClick={() => {
                      if (card.link) {
                        window.open(card.link, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && card.link) {
                        e.preventDefault();
                        window.open(card.link, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <img
                      src={card.imgUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading={isNearCenter ? 'eager' : 'lazy'}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="absolute bottom-[90px] sm:bottom-[110px] lg:bottom-[140px] left-0 right-0 flex items-center justify-center gap-4 px-4 z-30">
            {/* Previous Button */}
            <button
              type="button"
              onClick={handlePrevious}
              className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/20"
              aria-label="Previous slide"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-white"
              >
                <path
                  d="M10 12L6 8L10 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Indicator Dots */}
            <div className="flex items-center gap-2">
              {ProjectCards.map((_, index) => {
                const actualIndex = currentIndex % ProjectCards.length;
                const isActive = index === actualIndex;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDotClick(index)}
                    className={`transition-all duration-300 rounded-full ${
                      isActive ? 'w-8 h-2 bg-white' : 'w-2 h-2 bg-white/40 hover:bg-white/60'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-current={isActive ? 'true' : 'false'}
                  />
                );
              })}
            </div>

            {/* Next Button */}
            <button
              type="button"
              onClick={handleNext}
              className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/20"
              aria-label="Next slide"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-white"
              >
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Stats - Bottom */}
          <div className="absolute bottom-[20px] sm:bottom-[30px] lg:bottom-[50px] left-0 right-0 flex items-center justify-around px-4 lg:px-8 z-30">
            {/* 100k+ users */}
            <div className="text-center">
              <p className="text-white text-lg sm:text-xl lg:text-2xl xl:text-[34px] font-medium leading-[1.2] mb-0.5 lg:mb-1">
                100K+
              </p>
              <p className="text-white text-xs sm:text-sm lg:text-[16px] font-normal opacity-80">Users</p>
            </div>
            {/* 255k+ Apps */}
            <div className="text-center">
              <p className="text-white text-lg sm:text-xl lg:text-2xl xl:text-[34px] font-medium leading-[1.2] mb-0.5 lg:mb-1">
                255K+
              </p>
              <p className="text-white text-xs sm:text-sm lg:text-[16px] font-normal opacity-80">Apps</p>
            </div>
            {/* 100+ Countries */}
            <div className="text-center">
              <p className="text-white text-lg sm:text-xl lg:text-2xl xl:text-[34px] font-medium leading-[1.2] mb-0.5 lg:mb-1">
                100+
              </p>
              <p className="text-white text-xs sm:text-sm lg:text-[16px] font-normal opacity-80">Countries</p>
            </div>
          </div>
        </div>

        {/* Right Panel - Hero Content + Login Form */}
        <div className="px-2 lg:px-4 ml-0 md:-ml-12 -mt-12 lg:mt-16 w-full max-w-md sm:max-w-lg lg:flex-1 lg:max-w-none lg:flex lg:items-center lg:justify-center">
          {view === 'landing' ? (
            <div className="flex flex-col gap-[2.5rem] items-center justify-center pt-20 lg:pt-0 lg:ml-0">
              {/* Hero Content - Show on mobile with adjusted styles, always visible */}
              <div className="z-50 flex items-center gap-3 sm:gap-3">
                <img src="/images/dualite-logo.svg" alt="Dualite" className="w-12 sm:w-10" loading="eager" />
                <img src="/images/alpha-logo.svg" alt="Alpha" className="h-4 sm:h-4" loading="eager" />
              </div>
              <div id="onboarding-hero-content" className="flex flex-col gap-3 sm:gap-4 items-center w-full sm:w-fit">
                {/* Green Pill Badge */}

                {/* Main Heading */}
                <h1
                  style={{ fontFamily: 'Instrument Serif' }}
                  className="text-[#fdfdfd] text-2xl sm:text-3xl lg:text-[40px] font-normal leading-[1.2] tracking-[0] text-center bg-gradient-to-b from-[#A7ADB7] via-[#D5DBE6] to-[#DBE0E7] bg-clip-text text-transparent"
                >
                  Build websites and apps in minutes
                </h1>

                {/* Subtitle */}
                <p className="bg-gradient-to-b from-[#A7ADB7] via-[#D5DBE6] to-[#DBE0E7] bg-clip-text text-transparent text-sm sm:text-base lg:text-md font-normal leading-[1.2]">
                  World&apos;s fastest way to turn your idea to app in minutes
                </p>

                <GreenPill icon={IconsMapping.rocket}>
                  <span>Build without any limits with our </span>
                  <span
                    className="underline cursor-pointer"
                    role="link"
                    tabIndex={0}
                    onClick={() => {
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        e.preventDefault();
                      }
                    }}
                  >
                    unlimited messages plan
                  </span>
                </GreenPill>
              </div>

              {/* Login Form */}
              <GoogleOnboarding
                handleEmailSubmit={handleEmailSubmit}
                email={email}
                setEmail={setEmail}
                isCheckingEmail={isCheckingEmail}
                isBlocked={isBlocked}
              />

              {/* Scroll Down Indicator */}
              <div className="scroll-indicator flex flex-col items-center gap-2 mt-16">
                <p className="text-[rgba(253,253,253,0.6)] text-sm sm:text-base font-normal tracking-wide">
                  Scroll to know more
                </p>
                <div className="scroll-arrow">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-[rgba(253,253,253,0.6)]"
                  >
                    <path
                      d="M12 5V19M12 19L19 12M12 19L5 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ) : (
            <EmailOnboarding
              email={email}
              handleBackToLogin={handleBackToLogin}
              isInitialSending={isSendingOTP}
              setSuccess={setSuccess}
              success={success}
            />
          )}
        </div>
      </div>

      {/* Feature Showcase Section */}
      {/* <FeatureShowcase /> */}
      <FeatureShowcaseNew />

      {/* Pricing Section */}
      <PricingSection
        currency={currency}
        onScrollToTop={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onContactSales={() => window.open('https://calendly.com/singhvirohan/dualite-feedback', '_blank')}
      />

      {/* FAQ Section */}
      <div id="faqs" className="w-full bg-[#10110C] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 scroll-mt-4">
        <div className="max-w-3xl mx-auto">
          <h2
            className="text-white text-3xl sm:text-4xl lg:text-5xl font-semibold text-center mb-12 lg:mb-16 group relative block mx-auto"
            style={{ fontFamily: 'Inter Display' }}
          >
            Your questions answered
            {/* <a 
              href="#faqs"
              className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-white/40 hover:text-white/60 no-underline"
              aria-label="Link to this section"
            >
              #
            </a> */}
          </h2>

          <div className="space-y-0">
            {faqData.map((faq, index) => (
              <div key={index} className="border-b border-white/10">
                <button
                  type="button"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={openFaqIndex === index}
                  aria-controls={`faq-panel-${index}`}
                  className="w-full flex items-center justify-between py-6 text-left group hover:opacity-80 transition-opacity"
                >
                  <span
                    className="text-white text-base sm:text-lg lg:text-xl font-normal pr-4"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    {faq.question}
                  </span>
                  <svg
                    className={`w-5 h-5 text-white/70 flex-shrink-0 transition-transform duration-300 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  id={`faq-panel-${index}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFaqIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                  }`}
                >
                  <p
                    className="text-white/70 text-sm sm:text-base lg:text-lg leading-relaxed"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Support Contact Line */}
          <p className="text-white/60 text-center text-sm sm:text-base mt-12" style={{ fontFamily: 'Inter Display' }}>
            Can't find what you're looking for?{' '}
            <span className="block sm:inline py-2">
              Contact our{' '}
              <a href="mailto:support@dualite.dev" className="text-white font-medium hover:underline transition-all">
                customer support team
              </a>
            </span>
          </p>
        </div>
      </div>

      {/* Footer Section */}
      <OnboardingFooter />
    </div>
  );
}
